import type { ParseKeys } from 'i18next'

import type { AccessRead } from '@/client'
import type { RouteZoneKeys } from '@/hooks/useZone'

import {
  FOOTER,
  hasPages,
  panelGroups,
  trailFor,
  visibleRuns,
  type NavLink,
  type Section,
} from './navigation'

/**
 * Time zones follow the sidebar.
 *
 * A module — a first-level entry of the sidebar, Advertising or Business —
 * has one zone, and every page in it reads its times in that zone. A page in
 * the module can be given a zone of its own, which wins over its module's. So
 * an ads team sets Advertising to the ads account's zone once, and a person
 * who wants one report in their own zone sets just that page.
 *
 * The keys are what `/v1/me/timezones` stores, taken from the sidebar rather
 * than chosen by each page, so the dialog that sets them and the page that
 * reads them can never disagree:
 *
 * - a page is its link, `/marketing/ams/history` → `marketing.ams.history`
 *   (the dashboard, at `/`, is `dashboard`);
 * - a module is `module.` and its name, `nav.marketing` → `module.marketing`.
 *
 * A module that is a single page — Dashboard, Tickets, AI Listing — has no
 * pages beneath it to differ, so it is one row keyed as the page.
 *
 * A page reached from a sidebar page — a ticket from Tickets, a job from AI
 * Listing — is that page for this purpose: `trailFor` names the link a path
 * sits under, so ticketing is one zone wherever in it the reader is.
 *
 * Renaming a link moves its key, which orphans the choices people made for it;
 * they fall back to the module's, which is the least surprising thing to lose.
 */

/** The zone key for a sidebar page. Lower-case, dotted: the backend's page-key pattern. */
export function pageZoneKey(to: string): string {
  if (to === '/') return 'dashboard'
  return to.replace(/^\//, '').replace(/\//g, '.').toLowerCase()
}

/** The zone key for a sidebar module. */
export function moduleZoneKey(section: Section): string {
  return `module.${section.key.replace(/^nav\./, '').toLowerCase()}`
}

/** Where a path sits for its zone: the module and page keys, and their sidebar names. */
export type RouteZone = RouteZoneKeys

export const NO_ROUTE: RouteZone = { module: null, page: null }

/** The module and page a path belongs to, as the sidebar files it. */
export function routeZone(pathname: string): RouteZone {
  const trail = trailFor(pathname)
  // The sidebar's foot is the person's own (My access), not a module of work:
  // it has no zone of its own and reads the default.
  if (!trail || FOOTER.includes(trail.section)) return NO_ROUTE
  const { section, page } = trail
  if (!hasPages(section)) {
    return {
      module: null,
      page: pageZoneKey(section.to),
      moduleLabel: section.key,
      pageLabel: section.key,
    }
  }
  return {
    module: moduleZoneKey(section),
    page: page ? pageZoneKey(page.to) : null,
    moduleLabel: section.key,
    ...(page ? { pageLabel: page.key } : {}),
  }
}

export type ZonePage = { key: string; label: ParseKeys; icon?: string | undefined }

/** A sidebar module, for the dialog: its own row, and its pages in their titled runs. */
export type ZoneModule = {
  section: Section
  /**
   * The key the module's own row sets: `module.*` for a module with pages, the
   * page's key for a single-page module.
   */
  key: string
  /** Its pages, in the sidebar's runs and order; empty for a single-page module. */
  runs: { key?: ParseKeys; pages: ZonePage[] }[]
}

/**
 * Every module the reader is offered, in the sidebar's order, with the pages
 * the sidebar shows them — a page they cannot open, or one not built yet, is
 * not one they need a zone for. The sidebar's foot (My access, Forum,
 * Settings) is the person's own, not a module of work, so it is left out.
 */
export function zoneModules(access: AccessRead | undefined): ZoneModule[] {
  const sections = visibleRuns(access).flatMap((run) => run.sections)
  return sections.flatMap((section): ZoneModule[] => {
    if (!hasPages(section)) {
      return section.soon ? [] : [{ section, key: pageZoneKey(section.to), runs: [] }]
    }
    const runs = panelGroups(section)
      .map((run) => ({
        ...(run.key ? { key: run.key } : {}),
        pages: run.links
          .filter((link: NavLink) => !link.soon)
          .map((link) => ({ key: pageZoneKey(link.to), label: link.key, icon: link.icon })),
      }))
      .filter((run) => run.pages.length > 0)
    return runs.length > 0 ? [{ section, key: moduleZoneKey(section), runs }] : []
  })
}
